/**
 * J5 — the location-truth card (#948, behind `field_redesign_v2`).
 *
 * A device can carry two different answers to "where is it": the one on its
 * own record, and the one the work order reported (`wo_reported_location`,
 * migration 531). The server has derived `location_conflict` at read time
 * since #819, and `POST /jobs/:jobId/devices/:deviceId/settle-location` has
 * existed to settle it — **and nothing in the client has ever called either.**
 * So today the tech sees one location, silently, and is sent to the wrong side
 * of a building on the office's word with no way to say otherwise.
 *
 * This card is the missing half. It names both answers, asks the person
 * standing there which is right, and their tap IS the settlement: the endpoint
 * writes the device record and stamps the acknowledgement, so the conflict is
 * gone for good rather than re-asked every year.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, MapPin } from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { locationTruthCard } from "../redesign-copy";

export interface ConflictedDevice {
    assetId: string;
    label: string;
    deviceLocation: string | null;
    workOrderLocation: string | null;
}

export function LocationTruthCard({
    jobId,
    device,
    onSettled,
}: {
    jobId: string;
    device: ConflictedDevice;
    /** Called after a successful settle so the caller can refetch. */
    onSettled?: () => void;
}) {
    const qc = useQueryClient();
    const [settling, setSettling] = useState<string | null>(null);

    const copy = locationTruthCard({
        deviceLabel: device.label,
        deviceLocation: device.deviceLocation,
        workOrderLocation: device.workOrderLocation,
    });

    const settle = useMutation({
        mutationFn: (locationDescription: string) =>
            api.post(`/api/jobs/${jobId}/devices/${device.assetId}/settle-location`, { locationDescription }),
        onSuccess: () => {
            toast.success("Thanks — that's the record now.");
            void qc.invalidateQueries({ queryKey: ["field", "job-detail", jobId] });
            onSettled?.();
        },
        onError: () => toast.error("Couldn't save that — try again in a sec."),
        onSettled: () => setSettling(null),
    });

    // Nothing genuinely in conflict → the card must not exist. (The server's
    // flag and this check have to agree; this is the belt to its braces.)
    if (!copy) return null;

    return (
        <Card
            className="border-amber-400 bg-amber-50/60 dark:border-amber-500/40 dark:bg-amber-950/20"
            data-testid="location-truth-card"
        >
            <CardContent className="space-y-2 p-4">
                <p className="flex items-center gap-1.5 text-sm font-semibold">
                    <MapPin className="h-4 w-4 shrink-0" />
                    {copy.title}
                </p>
                <p className="text-sm text-foreground/85">{copy.body}</p>
                <div className="flex flex-col gap-2 pt-1">
                    {copy.choices.map((c) => (
                        <Button
                            key={c.key}
                            variant="outline"
                            className="h-auto min-h-[48px] justify-start gap-2 whitespace-normal py-2 text-left"
                            disabled={settle.isPending}
                            data-testid={`settle-${c.key}`}
                            onClick={() => {
                                setSettling(c.key);
                                settle.mutate(c.value);
                            }}
                        >
                            {settle.isPending && settling === c.key ? (
                                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                            ) : (
                                <MapPin className="h-4 w-4 shrink-0" />
                            )}
                            {c.label}
                        </Button>
                    ))}
                </div>
                <p className="text-xs text-foreground/70">{copy.footnote}</p>
            </CardContent>
        </Card>
    );
}
